import { useEffect, useState } from 'react';

export interface NetworkStatus {
  /** True while the browser reports an active connection. */
  online: boolean;
  /** Becomes true the first time `online` flips false in this session. */
  wasOffline: boolean;
}

const isBrowser = typeof window !== 'undefined' && typeof navigator !== 'undefined';

/**
 * Tracks the browser's online/offline state with optimistic defaults so SSR
 * (and very early renders before navigator is wired up) don't flash an
 * "offline" banner. The `wasOffline` flag lets consumers show a transient
 * "back online" toast only when there's actually been a recovery.
 */
export function useNetworkStatus(): NetworkStatus {
  const [online, setOnline] = useState<boolean>(() => (isBrowser ? navigator.onLine : true));
  const [wasOffline, setWasOffline] = useState<boolean>(false);

  useEffect(() => {
    if (!isBrowser) return;
    const handleOnline = () => {
      setOnline(true);
    };
    const handleOffline = () => {
      setOnline(false);
      setWasOffline(true);
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { online, wasOffline };
}
